<template>
  <!--
    DESIGN.md `chip` (the grade-badge shape): a solid surface-raised pill with a
    `border` edge and the tone's `*-ink` label. No tint, no alpha (Ben Q42).
    Quasar's `rounded` makes the pill; tokens only, so it follows the theme.
  -->
  <q-badge rounded class="status-badge g-type-chip" :class="`status-badge--${tone}`">
    <slot>{{ label }}</slot>
  </q-badge>
</template>

<script lang="ts">
export type StatusBadgeTone = 'accent' | 'positive' | 'warning' | 'negative' | 'neutral';

/** Maps a Quasar status colour name ("positive", "grey", …) to a badge tone. */
export function statusBadgeTone(color: string | null | undefined): StatusBadgeTone {
  if (color === 'positive' || color === 'negative' || color === 'warning') return color;
  if (color === 'primary' || color === 'accent') return 'accent';
  return 'neutral';
}
</script>

<script setup lang="ts">
withDefaults(
  defineProps<{
    tone?: StatusBadgeTone;
    label?: string;
  }>(),
  { tone: 'accent', label: '' }
);
</script>

<style scoped lang="scss">
.status-badge {
  --status-ink: var(--g-accent-ink);

  background: var(--g-bg-elevated);
  border: 1px solid var(--g-border-subtle);
  color: var(--status-ink);
  line-height: 1;
  padding: var(--space-1) var(--space-2);
  white-space: nowrap;
}

.status-badge--positive {
  --status-ink: var(--g-positive-ink);
}

.status-badge--warning {
  --status-ink: var(--g-warning-ink);
}

.status-badge--negative {
  --status-ink: var(--g-negative-ink);
}

.status-badge--neutral {
  --status-ink: var(--g-text-secondary);
}
</style>
